import {useState} from 'react'
import {View} from 'react-native'

import {atoms as a} from '#/alf'
import * as Tooltip from '#/components/Tooltip'
import {Text} from '#/components/Typography'

/**
 * The words behind an icon-only badge, on a pointer.
 *
 * A badge is a count in a glyph's clothing — deliberately wordless, because the
 * footer it sits in has one number a reader is scanning for and four words would
 * push it off the end — and this is where the words went instead. Opening on hover
 * rather than on press is the whole point: a badge is being *asked* what it says,
 * not activated, and a press that opened a bubble would fight the badge that also
 * opens a page's questions.
 *
 * Drawn through the app's own tooltip so a document's badges look like every other
 * explained control, rather than like the browser's grey rectangle.
 */
export function BadgeTooltip({
  label,
  children,
}: {
  /** The words, said exactly as the accessibility label says them. */
  label: string
  children: React.ReactNode
}) {
  const [hovered, setHovered] = useState(false)
  return (
    <Tooltip.Outer
      visible={hovered}
      onVisibleChange={setHovered}
      position="top">
      <Tooltip.Target>
        <View
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}>
          {children}
        </View>
      </Tooltip.Target>
      {/*
        The content is labelled with the sentence rather than handed
        `Tooltip.TextBubble`, which builds its own label out of its children and
        would name this bubble "[object Object]".
      */}
      <Tooltip.Content label={label}>
        <Text style={[a.text_sm, a.leading_snug]}>{label}</Text>
      </Tooltip.Content>
    </Tooltip.Outer>
  )
}
